/**
 * Geometría compartida de los lienzos de cableado.
 *
 * Vive aparte del diseñador de switches porque el Diseñador de Cableado usa
 * exactamente la misma tarjeta de puertos y el mismo enrutado de cables. Son
 * funciones PURAS (sin React ni estado) para poder probarlas aisladas: si se
 * toca aquí, se rompen los dos lienzos a la vez y el test lo canta.
 */

// ── Geometría del panel de puertos ──────────────────────────────────────────
export const PORT_W = 15;
export const PORT_H = 15;
export const PORT_GAP = 5;
export const MAX_COLS = 24;
export const CARD_PAD = 14;
// Columna izquierda con nombre, modelo y estado. Al no ocupar la franja
// superior/inferior de los puertos, los cables pueden entrar por el borde.
export const INFO_W = 132;
export const INFO_MIN_H = 62;
// Franja libre entre el borde de la tarjeta y la primera / última fila de
// puertos: por ahí sube y baja el latiguillo sin cruzar nada.
export const ENTRY_GAP = 18;
export const PORT_LABEL_H = 11;
export const ROW_GAP = 14;
export const CARD_MIN_W = 205;
export const EMPTY_CARD_H = 74;

// Rejilla de «Auto-organizar»: huecos amplios para que los cables quepan en el
// espacio entre tarjetas (el trazado corre por arriba/abajo y por los laterales).
export const HGAP = 150;
export const VGAP = 190;
export const ORIGIN = 30;

// Enrutado de cables: separación del carril al borde de la tarjeta y distancia
// entre carriles de cables paralelos (cada cable corre por el suyo).
export const TRUNK_GAP = 20;
export const LANE_STEP = 9;

export const ZMIN = 0.2;
export const ZMAX = 2.5;

export const CABLE_TYPES = [
  'Cat5e', 'Cat6', 'Cat6a', 'Cat7', 'Cat8',
  'Fibra MM (OM3/OM4)', 'Fibra SM (OS2)',
  'DAC (cobre directo)', 'AOC (fibra activa)', 'Otro',
];

export const CABLE_COLORS = [
  { value: 'negro', label: 'Negro', hex: '#1f2937' },
  { value: 'gris', label: 'Gris', hex: '#9ca3af' },
  { value: 'blanco', label: 'Blanco', hex: '#e5e7eb' },
  { value: 'rojo', label: 'Rojo', hex: '#ef4444' },
  { value: 'azul', label: 'Azul', hex: '#3b82f6' },
  { value: 'verde', label: 'Verde', hex: '#22c55e' },
  { value: 'amarillo', label: 'Amarillo', hex: '#eab308' },
  { value: 'naranja', label: 'Naranja', hex: '#f97316' },
  { value: 'violeta', label: 'Violeta', hex: '#8b5cf6' },
  { value: 'celeste', label: 'Celeste', hex: '#38bdf8' },
];

export const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export function colorHex(name) {
  return CABLE_COLORS.find((c) => c.value === name)?.hex ?? '#3b82f6';
}

export function isFiber(cableType) {
  return /fibra|fiber|sm|mm|aoc/i.test(cableType || '');
}

/** Etiqueta de un puerto: `label` o `P<n>`. Acepta puertos del grafo y de la ficha. */
export function portLabel(port) {
  if (!port) return '—';
  return port.label || `P${port.number ?? port.port_number ?? '?'}`;
}

export function truncate(text, max) {
  const s = String(text ?? '');
  return s.length > max ? `${s.slice(0, Math.max(1, max - 1))}…` : s;
}

/** Reparto manual de filas: [12, 12] → dos filas. null = automático. */
export function normalizeRows(portCount, rowSizes) {
  if (!Array.isArray(rowSizes) || !rowSizes.length) return null;
  const rows = [];
  let remaining = portCount;
  for (const raw of rowSizes) {
    if (remaining <= 0) break;
    const n = Math.floor(Number(raw) || 0);
    if (n <= 0) continue;
    const take = Math.min(n, remaining);
    rows.push(take);
    remaining -= take;
  }
  // Si el switch crece, los puertos nuevos van a una última fila.
  if (remaining > 0) rows.push(remaining);
  return rows.length ? rows : null;
}

/** Rejilla del panel: nº de columnas, filas y tamaño de la tarjeta. */
export function portGrid(portCount, rowSizes, numberingMode) {
  const numbering = numberingMode === 'alternate' ? 'alternate' : 'rows';
  if (!portCount) {
    return {
      cols: 0, rows: 0, width: CARD_MIN_W, height: EMPTY_CARD_H,
      gridW: 0, rowH: 0, padX: INFO_W + CARD_PAD, infoW: INFO_W, rowSizes: [], numbering,
    };
  }
  const autoCols = portCount <= 12 ? portCount : MAX_COLS;
  const forced = normalizeRows(portCount, rowSizes);
  let sizes = forced || Array.from({ length: Math.ceil(portCount / autoCols) }, () => autoCols);
  if (numbering === 'alternate' && !forced && portCount > 12) {
    // Sin filas definidas, la numeración intercalada implica dos filas.
    sizes = [Math.ceil(portCount / 2), Math.floor(portCount / 2)].filter((n) => n > 0);
  }
  if (numbering === 'alternate') {
    // Impares en la primera fila, pares en la segunda: el ancho lo marca el
    // número de columnas, no el reparto manual.
    const rowsN = sizes.length;
    const colsAlt = Math.ceil(portCount / rowsN);
    sizes = Array.from({ length: rowsN }, (_, i) => Math.min(colsAlt, portCount - i * colsAlt))
      .filter((n) => n > 0);
  }
  const cols = Math.max(...sizes);
  const rows = sizes.length;
  const gridW = cols * PORT_W + (cols - 1) * PORT_GAP;
  const rowH = PORT_H + PORT_LABEL_H + ROW_GAP;
  const gridH = rows * rowH - ROW_GAP;
  const width = Math.max(CARD_MIN_W, INFO_W + CARD_PAD * 2 + gridW);
  const height = Math.max(INFO_MIN_H, ENTRY_GAP * 2 + gridH);
  const padX = INFO_W + CARD_PAD + Math.max(0, (width - (INFO_W + CARD_PAD * 2 + gridW)) / 2);
  return { cols, rows, rowSizes: sizes, numbering, width, height, gridW, rowH, padX, infoW: INFO_W };
}

/** Fila y columna que ocupa el puerto `index` según el reparto y la numeración. */
export function portSlot(geo, index) {
  const sizes = geo.rowSizes || [];
  if (!sizes.length) return { row: 0, col: 0 };
  if (geo.numbering === 'alternate') {
    // 1,3,5… arriba y 2,4,6… abajo (recorrido en serpentina por columnas).
    const rows = sizes.length;
    return { row: index % rows, col: Math.floor(index / rows) };
  }
  let remaining = index;
  for (let r = 0; r < sizes.length; r += 1) {
    if (remaining < sizes[r]) return { row: r, col: remaining };
    remaining -= sizes[r];
  }
  const last = Math.max(0, sizes.length - 1);
  return { row: last, col: Math.max(0, (sizes[last] || 1) - 1) };
}

/** Centro del puerto relativo a la esquina superior izquierda de la tarjeta. */
export function portOffset(geo, index) {
  const { row, col } = portSlot(geo, index);
  return {
    x: geo.padX + col * (PORT_W + PORT_GAP) + PORT_W / 2,
    y: ENTRY_GAP + row * geo.rowH + PORT_H / 2,
  };
}

/**
 * Borde por el que sale el latiguillo: -1 arriba, +1 abajo. Con varias filas,
 * la primera sale por arriba y el resto por abajo, de modo que ningún cable
 * cruza la fila de arriba. Con una sola fila se elige el borde más cercano al
 * otro extremo del cable.
 */
export function exitSide(geo, index, selfY, otherY) {
  if (geo.rows > 1) return portSlot(geo, index).row === 0 ? -1 : 1;
  return otherY < selfY ? -1 : 1;
}

/** Path con esquinas redondeadas a partir de una polilínea ortogonal. */
export function roundedPath(points, radius = 16) {
  // Sin puntos repetidos: un tramo de longitud 0 deja `Math.sign(0)=0` y una
  // curva de radio 0 que se veía como un nudo.
  const pts = [];
  (points || []).forEach((p) => {
    if (!p) return;
    const last = pts[pts.length - 1];
    if (last && Math.abs(last.x - p.x) < 0.01 && Math.abs(last.y - p.y) < 0.01) return;
    pts.push(p);
  });
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const prev = pts[i - 1];
    const cur = pts[i];
    const next = pts[i + 1];
    const inLen = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const outLen = Math.hypot(next.x - cur.x, next.y - cur.y);
    const inDir = { x: Math.sign(cur.x - prev.x), y: Math.sign(cur.y - prev.y) };
    const outDir = { x: Math.sign(next.x - cur.x), y: Math.sign(next.y - cur.y) };
    // Sigue recto: no hay esquina que redondear.
    if (inDir.x === outDir.x && inDir.y === outDir.y) {
      d += ` L ${cur.x} ${cur.y}`;
      continue;
    }
    const r = Math.min(radius, inLen / 2, outLen / 2);
    const p1 = { x: cur.x - inDir.x * r, y: cur.y - inDir.y * r };
    const p2 = { x: cur.x + outDir.x * r, y: cur.y + outDir.y * r };
    d += ` L ${p1.x} ${p1.y} Q ${cur.x} ${cur.y} ${p2.x} ${p2.y}`;
  }
  const last = pts[pts.length - 1];
  return `${d} L ${last.x} ${last.y}`;
}

/** Punto medio del tramo más largo: ahí se cuelga la etiqueta del cable. */
export function polylineMid(points) {
  let bestIndex = 0;
  let bestLen = -1;
  for (let i = 0; i < points.length - 1; i += 1) {
    const len = Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
    if (len > bestLen) { bestLen = len; bestIndex = i; }
  }
  const a = points[bestIndex];
  const b = points[bestIndex + 1] || a;
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Recorrido ortogonal de un cable. Depende SOLO de las dos tarjetas que une
 * (`rectA`/`rectB`), nunca de otras: sale VERTICAL del borde de cada una
 * (arriba o abajo, según `sideA`/`sideB`: -1 arriba, +1 abajo), corre por un
 * carril horizontal propio (`lane` lo separa de los paralelos) y entra
 * vertical en el otro extremo. Si el tramo vertical cruzaría la tarjeta del
 * OTRO extremo, la BORDEA por el lado más cercano.
 *
 * (ax, ay) y (bx, by) son los puntos de SALIDA en el borde de cada tarjeta.
 */
export function cableRoute(ax, ay, bx, by, sideA, sideB, lane, rectA, rectB, obstacles = []) {
  const off = TRUNK_GAP + lane * LANE_STEP;

  // El carril debe quedar por FUERA de la tarjeta de cada extremo y del lado
  // por el que sale el cable (`ay`/`by` ya son esos bordes). Si un obstáculo
  // lo empuja al otro lado de una de las dos tarjetas, la vertical de conexión
  // la atraviesa y el cable parece salir por donde no toca: se acota.
  let lo = -Infinity;
  let hi = Infinity;
  if (sideA === 1) lo = Math.max(lo, ay); else hi = Math.min(hi, ay);
  if (sideB === 1) lo = Math.max(lo, by); else hi = Math.min(hi, by);

  const boundTrunk = (y) => {
    let out = y;
    if (lo !== -Infinity) out = Math.max(out, lo + 2);
    if (hi !== Infinity) out = Math.min(out, hi - 2);
    return out;
  };

  // El carril horizontal no debe atravesar NINGUNA tarjeta que pille en medio
  // (en el lienzo de cableado hay muchas y si no, el cable las cruza por
  // encima). Se empuja al hueco libre más cercano, por arriba o por abajo,
  // sin salirse del pasillo válido.
  const avoidCards = (y, fromX = ax, toX = bx, keepSide = true) => {
    if (!obstacles.length) return y;
    const x1 = Math.min(fromX, toX) - 2;
    const x2 = Math.max(fromX, toX) + 2;
    const nearby = obstacles.filter(
      (o) => o && o !== rectA && o !== rectB && o.x2 > x1 && o.x1 < x2,
    );
    if (!nearby.length) return y;
    let cur = y;
    for (let guard = 0; guard < 12; guard += 1) {
      let hit = null;
      for (let i = 0; i < nearby.length; i += 1) {
        const o = nearby[i];
        if (cur > o.y1 - 4 && cur < o.y2 + 4) { hit = o; break; }
      }
      if (!hit) return cur;
      const above = hit.y1 - 4 - lane * 2;
      const below = hit.y2 + 4 + lane * 2;
      // Si los dos extremos salen hacia abajo, el carril vive por debajo de las
      // dos tarjetas y solo puede seguir bajando (subir lo metería por dentro).
      // En un tramo lateral, manda el hueco libre más cercano.
      const preferDown = keepSide && sideA === 1 && sideB === 1;
      const preferUp = keepSide && sideA === -1 && sideB === -1;
      const next = preferDown
        ? below
        : preferUp
          ? above
          : (Math.abs(cur - above) <= Math.abs(below - cur) ? above : below);
      // En el carril principal nunca se sale del pasillo entre las dos
      // tarjetas; si no cabe, es preferible cruzar ese obstáculo ajeno.
      const bounded = keepSide ? boundTrunk(next) : next;
      if (bounded === cur) return cur;
      cur = bounded;
    }
    return cur;
  };

  // Pasillo vertical libre de tarjetas (para el rodeo lateral).
  const freeTurnX = (base, dir, yTop, yBottom) => {
    if (!obstacles.length) return base;
    const lo = Math.min(yTop, yBottom);
    const hi = Math.max(yTop, yBottom);
    for (let i = 0; i <= 80; i += 1) {
      const x = base + dir * i * 8;
      const blocked = obstacles.some(
        (o) => o !== rectA && o !== rectB
          && x > o.x1 - 1 && x < o.x2 + 1
          && hi > o.y1 - 1 && lo < o.y2 + 1,
      );
      if (!blocked) return x;
    }
    return base;
  };

  // Según salgan los dos extremos (los límites `lo`/`hi` ya están calculados):
  let trunkY;
  if (hi === Infinity) trunkY = lo + off;            // los dos por abajo
  else if (lo === -Infinity) trunkY = hi - off;      // los dos por arriba
  else if (lo + 24 < hi) trunkY = Math.min(lo + off, (lo + hi) / 2); // de frente
  else {
    // Hueco vertical escaso. Si las tarjetas se miran de frente y NO se
    // solapan, se cruza por el hueco entre las dos: antes se rodeaba por
    // fuera y, con los bordes casi pegados, el cable daba un giro en U que
    // se veía como un nudo.
    const facingDown = sideA === 1 && sideB === -1 && rectA.y2 <= rectB.y1;
    const facingUp = sideA === -1 && sideB === 1 && rectB.y2 <= rectA.y1;
    if (facingDown || facingUp) {
      const gapLo = facingDown ? rectA.y2 : rectB.y2;
      const gapHi = facingDown ? rectB.y1 : rectA.y1;
      const span = Math.max(0, gapHi - gapLo);
      // Cada cable a su altura dentro del hueco (los carriles siguen separando).
      const laneY = gapLo + 3 + lane * 4;
      trunkY = Math.min(Math.max(laneY, gapLo + 2), Math.max(gapLo + 2, gapHi - 2));
      if (span < 4) trunkY = gapLo + span / 2;
      return [
        { x: ax, y: ay }, { x: ax, y: trunkY },
        { x: bx, y: trunkY }, { x: bx, y: by },
      ];
    }

    // Salen en direcciones OPUESTAS y alejándose (p. ej. un puerto de la fila
    // de arriba del switch de arriba y uno de la fila de abajo del de abajo):
    // se rodea por un lateral, POR FUERA de las dos tarjetas (si no, el giro
    // caía dentro de ellas y las cruzaba).
    const yA = sideA === 1 ? rectA.y2 + off : rectA.y1 - off;
    const yB = sideB === 1 ? rectB.y2 + off : rectB.y1 - off;
    const rightX = Math.max(rectA.x2, rectB.x2) + TRUNK_GAP + lane * LANE_STEP;
    const leftX = Math.min(rectA.x1, rectB.x1) - TRUNK_GAP - lane * LANE_STEP;
    const preferRight = (rightX - ax) + (rightX - bx) <= (ax - leftX) + (bx - leftX);
    // Las dos horizontales y el pasillo vertical tienen que caer en zona libre:
    // si no, el rodeo pasaba por encima de las tarjetas de al lado.
    const spanLo = Math.min(ax, bx, preferRight ? rightX : leftX);
    const spanHi = Math.max(ax, bx, preferRight ? rightX : leftX);
    const clearA = avoidCards(yA, spanLo, spanHi, false);
    const clearB = avoidCards(yB, spanLo, spanHi, false);
    const turnX = preferRight
      ? freeTurnX(rightX, 1, clearA, clearB)
      : freeTurnX(leftX, -1, clearA, clearB);
    return [
      { x: ax, y: ay }, { x: ax, y: clearA }, { x: turnX, y: clearA },
      { x: turnX, y: clearB }, { x: bx, y: clearB }, { x: bx, y: by },
    ];
  }

  // Bajada/subida de cada extremo al carril, bordeando las tarjetas que la
  // fuesen a cruzar (la PROPIA también: si el carril ha acabado al otro lado
  // de ella, la vertical la atravesaba y el cable salía por donde no toca).
  trunkY = avoidCards(trunkY);
  const connector = (px, edgeY, other, self, towardX) => {
    const blockers = [];
    const check = (o) => {
      if (!o || blockers.includes(o)) return;
      const crossesX = px > o.x1 && px < o.x2;
      const crossesY = Math.max(edgeY, trunkY) > o.y1 && Math.min(edgeY, trunkY) < o.y2;
      if (crossesX && crossesY) blockers.push(o);
    };
    check(other);
    check(self);
    obstacles.forEach(check);
    if (!blockers.length) return [{ x: px, y: edgeY }, { x: px, y: trunkY }];

    const leftX = Math.min(...blockers.map((o) => o.x1)) - off;
    const rightX = Math.max(...blockers.map((o) => o.x2)) + off;
    const sideX = Math.abs(towardX - leftX) <= Math.abs(rightX - towardX) ? leftX : rightX;
    const clearY = edgeY < trunkY
      ? Math.max(edgeY, Math.min(...blockers.map((o) => o.y1)) - off)
      : Math.min(edgeY, Math.max(...blockers.map((o) => o.y2)) + off);
    return [
      { x: px, y: edgeY }, { x: px, y: clearY },
      { x: sideX, y: clearY }, { x: sideX, y: trunkY },
    ];
  };

  const ptsA = connector(ax, ay, rectB, rectA, bx);
  const ptsB = connector(bx, by, rectA, rectB, ax);
  return [...ptsA, ...ptsB.reverse()];
}

/** Coloca en rejilla las tarjetas que aún no tienen posición guardada. */
export function autoLayout(items, startY = ORIGIN) {
  const out = {};
  if (!items.length) return out;
  // Rejilla apaisada: más columnas que filas (las tarjetas son anchas y así
  // los cables entre filas tienen sitio y no se apelotona todo en vertical).
  const cols = Math.max(1, Math.min(items.length, Math.round(Math.sqrt(items.length) * 1.4)));
  let x = ORIGIN;
  let y = startY;
  let rowH = 0;
  let col = 0;
  items.forEach((item) => {
    if (col === 0) rowH = 0;
    out[item.id] = { x: Math.round(x), y: Math.round(y) };
    rowH = Math.max(rowH, item.geo.height);
    col += 1;
    if (col >= cols) {
      col = 0;
      x = ORIGIN;
      y += rowH + VGAP;
    } else {
      x += item.geo.width + HGAP;
    }
  });
  return out;
}
